import { inject, Injectable } from "@angular/core";
import { isPropertyDefined } from "@recursyve/nice-ts-utils";
import { NiceAsyncTypeaheadProvider, NiceAsyncTypeaheadSearchResult } from "@recursyve/nice-ui-kit.v2";
import { lastValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { getTranslation } from "../../../utils/i18n.utils";
import { SearchCategoriesDto } from "../dto/search-categories.dto";
import { CategoryLevels } from "../enums/category-levels.enum";
import { CategoryTypes } from "../enums/category-types.enum";
import { Categories } from "../models/categories.model";
import { CategoriesApi } from "./categories.api";

interface CategoriesTypeaheadSearchOptions {
    categorySectionIds?: number[];
    distributorIds?: number[];
    ignoreIds?: number[];
    level?: CategoryLevels;
    parentCategoryIds?: number[];
    type?: CategoryTypes;
}

@Injectable()
export class CategoriesTypeaheadApi extends NiceAsyncTypeaheadProvider<Categories> {
    private readonly categoriesApi = inject(CategoriesApi);

    public readonly resource = "categories";

    private readonly pageSize = 20;

    public override async getById(id: number): Promise<Categories> {
        return lastValueFrom(this.categoriesApi.getById(id));
    }

    public override search(
        searchQuery: string,
        page: number,
        searchOptions: CategoriesTypeaheadSearchOptions
    ): Observable<NiceAsyncTypeaheadSearchResult<Categories>> {
        const dto: SearchCategoriesDto = {
            page,
            pageSize: this.pageSize,
            searchQuery
        };

        if (isPropertyDefined(searchOptions, "categorySectionIds")) {
            dto.categorySectionIds = searchOptions.categorySectionIds;
        }

        if (isPropertyDefined(searchOptions, "distributorIds")) {
            dto.distributorIds = searchOptions.distributorIds;
        }

        if (isPropertyDefined(searchOptions, "ignoreIds")) {
            dto.ignoreIds = searchOptions.ignoreIds;
        }

        if (isPropertyDefined(searchOptions, "level")) {
            dto.level = searchOptions.level;
        }

        if (isPropertyDefined(searchOptions, "parentCategoryIds")) {
            dto.parentCategoryIds = searchOptions.parentCategoryIds;
        }

        if (isPropertyDefined(searchOptions, "type")) {
            dto.type = searchOptions.type;
        }

        return this.categoriesApi.search(dto).pipe(
            map((items) => ({
                items,
                nextPage: items.length === this.pageSize ? page + 1 : null
            }))
        );
    }

    public override format(item: Categories): string {
        if (!isPropertyDefined(item, "name")) {
            return "";
        }

        if (typeof item.name === "string") {
            return item.name;
        }

        return getTranslation(item.name) ?? "";
    }
}
